import type { StructuredBackendError } from "./errors/types";

export const PAYMENT_OUTCOMES = [
  "paid_and_activated",
  "paid_activation_pending",
  "paid_activation_failed",
  "paid_invoice_pending",
  "failed",
  "pending",
  "cancelled",
  "expired",
  "abandoned",
  "refunded",
] as const;

export type PaymentOutcome = typeof PAYMENT_OUTCOMES[number];

export function isPaymentOutcome(value: unknown): value is PaymentOutcome {
  return PAYMENT_OUTCOMES.includes(value as PaymentOutcome);
}

export function paymentErrorMessage(
  payload: unknown,
  language: string,
  fallback: string,
): string {
  const record = payload && typeof payload === "object"
    ? payload as Record<string, unknown>
    : {};
  const message = record.message;
  if (message && typeof message === "object") {
    const localized = message as Record<string, unknown>;
    const key = language.toLowerCase().startsWith("ar") ? "ar" : "en";
    if (typeof localized[key] === "string" && localized[key]) return localized[key] as string;
  }
  if (typeof message === "string" && message) return message;
  if (typeof record.user_message === "string" && record.user_message) return record.user_message;
  return fallback;
}

export function paymentAppError(
  payload: unknown,
  language: string,
  fallback: string,
  userTitle: string,
): StructuredBackendError {
  const record = payload && typeof payload === "object"
    ? payload as Record<string, unknown>
    : {};
  const rawCode = typeof record.code === "string"
    ? record.code
    : typeof record.error === "string"
      ? record.error
      : "PAYMENT_CHECKOUT_FAILED";
  const correlationId = typeof record.correlation_id === "string" && record.correlation_id
    ? record.correlation_id
    : undefined;

  return {
    error_code: rawCode === "PAYMENT_SETUP_NOT_READY" ? "BILL-PAY-001" : rawCode,
    error_family: rawCode === "BILLING_CONFIRMATION_REQUIRED" || rawCode === "BILLING_INCOMPLETE"
      ? "validation"
      : "dependency_failure",
    user_title: userTitle,
    user_message: paymentErrorMessage(payload, language, fallback),
    retry_allowed: record.retryable === true,
    correlation_id: correlationId,
  };
}

export interface PaymentIntentKeyInput {
  ownerType: "organization" | "candidate";
  ownerId: string;
  paymentType: string;
  productKey: string;
  billingCycle?: string;
}

export interface AuthoritativeCreditPaymentState {
  transactionId: string;
  paymentStatus: string | null | undefined;
  creditPurchaseStatus?: string | null;
}

const paymentIntentStorageKey = (input: PaymentIntentKeyInput): string => {
  const stable = [
    "fork",
    input.ownerType,
    input.ownerId,
    input.paymentType,
    input.productKey,
    input.billingCycle ?? "once",
  ].join(":");
  return `payment-intent:${stable}`;
};

const transactionStorageKey = (transactionId: string): string =>
  `payment-intent-transaction:${transactionId}`;

export function paymentIntentKey(input: PaymentIntentKeyInput): string {
  const storageKey = paymentIntentStorageKey(input);
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export function clearPaymentIntentKey(input: PaymentIntentKeyInput): void {
  sessionStorage.removeItem(paymentIntentStorageKey(input));
}

export function bindPaymentIntentKeyToTransaction(
  input: PaymentIntentKeyInput,
  transactionId: string | null | undefined,
): void {
  if (!transactionId) return;
  sessionStorage.setItem(transactionStorageKey(transactionId), paymentIntentStorageKey(input));
}

export function shouldRotatePaymentIntentKey(outcome: unknown): boolean {
  return [
    "paid_and_activated",
    "paid_invoice_pending",
    "failed",
    "cancelled",
    "expired",
    "abandoned",
    "refunded",
  ].includes(String(outcome ?? ""));
}

export function settlePaymentIntentKey(
  input: PaymentIntentKeyInput,
  outcome: unknown,
): boolean {
  if (!shouldRotatePaymentIntentKey(outcome)) return false;
  clearPaymentIntentKey(input);
  return true;
}

export function settlePaymentIntentKeyForTransaction(
  transactionId: string | null | undefined,
  outcome: unknown,
): boolean {
  if (!transactionId || !shouldRotatePaymentIntentKey(outcome)) return false;
  const mappingKey = transactionStorageKey(transactionId);
  const intentStorageKey = sessionStorage.getItem(mappingKey);
  if (!intentStorageKey) return false;
  sessionStorage.removeItem(intentStorageKey);
  sessionStorage.removeItem(mappingKey);
  return true;
}

export function authoritativeCreditPaymentOutcome(
  state: AuthoritativeCreditPaymentState,
): PaymentOutcome | null {
  const paymentStatus = String(state.paymentStatus ?? "");
  if (paymentStatus === "paid" && state.creditPurchaseStatus === "completed") {
    return "paid_and_activated";
  }
  if (shouldRotatePaymentIntentKey(paymentStatus)) {
    return paymentStatus as PaymentOutcome;
  }
  return null;
}

export function reconcileCreditPaymentIntentKey(
  state: AuthoritativeCreditPaymentState,
): boolean {
  const outcome = authoritativeCreditPaymentOutcome(state);
  if (!outcome) return false;
  return settlePaymentIntentKeyForTransaction(state.transactionId, outcome);
}

export function clearCompletedPaymentIntentKeys(): void {
  const keys: string[] = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith("payment-intent:") || key?.startsWith("payment-intent-transaction:")) {
      keys.push(key);
    }
  }
  keys.forEach((key) => sessionStorage.removeItem(key));
}
